import type { HazardsConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import { laneCenterX } from '../level/segments/segmentBuilders';
import { DEG_TO_RAD, lerp, smoothstep, TAU } from '../utils/math';
import { aimAt, bucketBoundsOf, solveArm, type ArmAngles, type PlaneBounds } from './excavatorArm';
import { HazardKind, type ExcavatorHazard } from './HazardDefinition';
import { colliderSlot, HazardCue, HazardPhase, setBox, type HazardModel, type HazardStepContext } from './HazardModel';

export type ExcavatorPose = {
  /** Arm direction in segment space (radians from +z towards +x). */
  slew: number;
  /** Joint angles relative to the rest pose (radians, positive = downwards). */
  boom: number;
  stick: number;
  bucket: number;
  /** Rubble heap in the lane, 0 … 1. */
  heap: number;
  /** Debris falling from the bucket, 0 … 1 while dumping (visual). */
  dump: number;
};

/** Where the arm should be: direction, bucket pivot reach/height from the slew centre, bucket curl. */
interface ArmTarget {
  slew: number;
  reach: number;
  height: number;
  curl: number;
}

interface Key {
  readonly time: number;
  readonly target: ArmTarget;
}

const target = (slew: number, reach: number, height: number, curl: number): ArmTarget => ({ slew, reach, height, curl });

/**
 * VEH_EXCAVATOR_A beside the track. Idle it digs behind the fence, arm turned away. After the
 * warning it lifts a full bucket, slews over the fence, lowers over the outer lane and dumps –
 * the rubble heap (jumpable) lies there `readyLead` seconds before the runner arrives. While the
 * bucket hangs over the lane, the lane is blocked from the bucket up (stick). Then it swings back.
 */
export class ExcavatorModel implements HazardModel<ExcavatorPose> {
  readonly kind = HazardKind.Excavator;
  phase: HazardPhase = HazardPhase.Idle;
  readonly pose: ExcavatorPose = { slew: 0, boom: 0, stick: 0, bucket: 0, heap: 0, dump: 0 };
  readonly previous: ExcavatorPose = { slew: 0, boom: 0, stick: 0, bucket: 0, heap: 0, dump: 0 };
  /** [0] bucket and stick, [1] rubble heap. */
  readonly colliders = [colliderSlot(), colliderSlot()];

  private readonly laneX: number;
  private readonly dumpSlew: number;
  private readonly dumpReach: number;
  private readonly idleSlew: number;
  private readonly keys: Key[] = [];
  private readonly arm: ArmTarget = target(0, 0, 0, 0);
  private readonly angles: ArmAngles = { boom: 0, stick: 0, bucket: 0 };
  private readonly bounds: PlaneBounds = { minForward: 0, maxForward: 0, minUp: 0, maxUp: 0 };
  private time = 0;
  private sequenceTime = 0;
  /** The dump happened – the excavator digs on, it never dumps twice. */
  private spent = false;
  private impacted = false;

  static startLead(config: HazardsConfig['excavator']): number {
    return config.telegraph + config.swingIn + config.lower + config.dump + config.readyLead;
  }

  constructor(
    private readonly spec: ExcavatorHazard,
    private readonly config: HazardsConfig['excavator'],
    world: WorldConfig,
  ) {
    this.laneX = laneCenterX(world, spec.lane);
    const aim = aimAt(config.rig, this.laneX - spec.machine.x, spec.z - spec.machine.z);
    this.dumpSlew = aim.slew;
    this.dumpReach = aim.reach;
    // Idle the arm points away from the track (left machine: forward-left, right machine: forward-right).
    this.idleSlew = aim.slew + Math.sign(spec.machine.x) * config.idle.slewAwayDegrees * DEG_TO_RAD;
    this.idleTarget(this.arm);
    this.apply();
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    Object.assign(this.previous, this.pose);
    const c = this.config;
    this.time += dt;
    if (this.phase === HazardPhase.Idle) {
      this.idleTarget(this.arm);
      if (!this.spent && ctx.armed && ctx.eta(this.spec.z) <= ExcavatorModel.startLead(c)) this.begin(cues);
    } else {
      this.sequenceTime += dt;
      this.sequence(cues);
    }
    this.apply();
  }

  /** Telegraph starts: key poses of the whole dump, beginning at the current digging pose. */
  private begin(cues: HazardCue[]): void {
    const c = this.config;
    const curlCarry = c.carryCurlDegrees * DEG_TO_RAD;
    const curlDump = c.dumpCurlDegrees * DEG_TO_RAD;
    const carryAtIdle = target(this.idleSlew, c.carryReach, c.carryPivotHeight, curlCarry);
    const carryAtLane = target(this.dumpSlew, c.carryReach, c.carryPivotHeight, curlCarry);
    const overLane = target(this.dumpSlew, this.dumpReach, c.dumpPivotHeight, curlCarry);
    const dumped = target(this.dumpSlew, this.dumpReach, c.dumpPivotHeight, curlDump);
    const steps: [number, ArmTarget][] = [
      [c.telegraph, carryAtIdle],
      [c.swingIn, carryAtLane],
      [c.lower, overLane],
      [c.dump, dumped],
      [c.hold, dumped],
      [c.swingOut, carryAtIdle],
    ];
    this.keys.length = 0;
    this.keys.push({ time: 0, target: { ...this.arm } });
    let time = 0;
    for (const [duration, pose] of steps) {
      time += duration;
      this.keys.push({ time, target: pose });
    }
    this.phase = HazardPhase.Warning;
    this.sequenceTime = 0;
    cues.push(HazardCue.Warning);
  }

  private sequence(cues: HazardCue[]): void {
    const c = this.config;
    const t = this.sequenceTime;
    if (this.phase === HazardPhase.Warning && t >= c.telegraph) this.phase = HazardPhase.Active;

    const dumpStart = c.telegraph + c.swingIn + c.lower;
    const dumpEnd = dumpStart + c.dump;
    // Debris leaves the bucket once it has opened a little; the heap grows as it lands.
    this.pose.dump = t >= dumpStart && t < dumpEnd ? (t - dumpStart) / c.dump : 0;
    const landing = c.heapDelay * c.dump;
    this.pose.heap = Math.max(this.pose.heap, smoothstep((t - dumpStart - landing) / (c.dump - landing)));
    if (!this.impacted && t >= dumpEnd) {
      this.impacted = true;
      this.pose.heap = 1;
      cues.push(HazardCue.Impact);
    }

    const last = this.keys[this.keys.length - 1]!;
    if (t < last.time) {
      this.sampleKeys(t, this.arm);
      return;
    }
    // Back into the digging loop.
    const blend = smoothstep((t - last.time) / c.settle);
    this.idleTarget(this.arm);
    this.mix(last.target, this.arm, blend, this.arm);
    if (blend >= 1) {
      this.phase = HazardPhase.Idle;
      this.spent = true;
    }
  }

  private sampleKeys(time: number, out: ArmTarget): void {
    for (let i = 1; i < this.keys.length; i++) {
      const to = this.keys[i]!;
      if (time > to.time) continue;
      const from = this.keys[i - 1]!;
      this.mix(from.target, to.target, smoothstep((time - from.time) / Math.max(to.time - from.time, 1e-6)), out);
      return;
    }
  }

  private mix(a: ArmTarget, b: ArmTarget, t: number, out: ArmTarget): void {
    out.slew = lerp(a.slew, b.slew, t);
    out.reach = lerp(a.reach, b.reach, t);
    out.height = lerp(a.height, b.height, t);
    out.curl = lerp(a.curl, b.curl, t);
  }

  /** Digging cycle behind the fence: reach out and down, curl, lift and pull back. */
  private idleTarget(out: ArmTarget): void {
    const { idle } = this.config;
    const phase = (TAU * this.time) / idle.period;
    out.slew = this.idleSlew;
    out.reach = lerp(idle.reach.min, idle.reach.max, 0.5 + 0.5 * Math.sin(phase));
    out.height = lerp(idle.pivotHeight.min, idle.pivotHeight.max, 0.5 + 0.5 * Math.cos(phase));
    out.curl = lerp(idle.curlDegrees.min, idle.curlDegrees.max, 0.5 - 0.5 * Math.cos(phase)) * DEG_TO_RAD;
  }

  /** Arm target → joint angles (pose) → colliders. */
  private apply(): void {
    const { rig, columnHeight, heap } = this.config;
    const arm = this.arm;
    const pivotHeight = rig.upperHeight + rig.boomPivot.up;
    solveArm(rig, arm.reach - rig.boomPivot.forward, arm.height - pivotHeight, arm.curl, this.angles);
    this.pose.slew = arm.slew;
    this.pose.boom = this.angles.boom;
    this.pose.stick = this.angles.stick;
    this.pose.bucket = this.angles.bucket;

    // Bucket footprint: outline along the arm × bucket width beside the arm plane.
    const b = bucketBoundsOf(rig, this.angles, this.bounds);
    const dirX = Math.sin(arm.slew);
    const dirZ = Math.cos(arm.slew);
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let corner = 0; corner < 4; corner++) {
      const forward = rig.boomPivot.forward + (corner < 2 ? b.minForward : b.maxForward);
      const lateral = rig.boomPivot.side + (corner % 2 === 0 ? -rig.bucket.width / 2 : rig.bucket.width / 2);
      // right of the arm direction = (cos, −sin)
      const x = this.spec.machine.x + dirX * forward + dirZ * lateral;
      const z = this.spec.machine.z + dirZ * forward - dirX * lateral;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    }
    const bottom = pivotHeight + b.minUp;
    setBox(this.colliders[0]!, minX, maxX, bottom, bottom + columnHeight, minZ, maxZ);

    const pile = this.colliders[1]!;
    if (this.pose.heap > 0) {
      setBox(pile, this.laneX - heap.width / 2, this.laneX + heap.width / 2, 0, heap.height * this.pose.heap, this.spec.z - heap.depth / 2, this.spec.z + heap.depth / 2);
    } else {
      pile.enabled = false;
    }
  }
}
